// src/components/WithdrawPage.jsx
import React, { useEffect, useState } from "react";
import MenuLayout from "./MenuLayout";
import "../styles/WithdrawPage.css";
import { createWithdrawal, getWithdrawals } from "../api/withdrawals";
import { getAddresses } from "../api/wallet";
import { getLiveAccounts } from "../api/challenge";
import { getReferral } from "../api/referrals";

const formatBalance = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("fa-IR");
};

const WithdrawPage = () => {
  const [form, setForm] = useState({
    withdrawal_type: "bank_transfer",
    account_number: "",
    wallet_address: "",
    challenge_account: "",
    total_profit: "",
    withdrawable_amount: "",
    description: "",
  });
  const [requests, setRequests] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [challengeAccounts, setChallengeAccounts] = useState([]);
  const [referralBalance, setReferralBalance] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadData = async () => {
    try {
      const [withdrawalsRes, addressesRes, challengesRes, referralRes] = await Promise.allSettled([
        getWithdrawals(),
        getAddresses(),
        getLiveAccounts(),
        getReferral(),
      ]);
      if (withdrawalsRes.status === "fulfilled") {
        setRequests(withdrawalsRes.value || []);
      } else {
        setError(withdrawalsRes.reason?.message || "خطا در دریافت اطلاعات برداشت");
      }
      if (addressesRes.status === "fulfilled") {
        setAddresses(addressesRes.value || []);
      }
      if (challengesRes.status === "fulfilled") {
        setChallengeAccounts(Array.isArray(challengesRes.value) ? challengesRes.value : []);
      }
      if (referralRes.status === "fulfilled") {
        const balance = referralRes.value?.withdrawable_amount;
        setReferralBalance(balance ?? null);
      }
    } catch (err) {
      setError(err.message || "خطا در دریافت اطلاعات برداشت");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSourceAccountChange = (value) => {
    const selected = challengeAccounts.find(
      (account) => String(account.account_number) === String(value)
    );
    const rawProfit =
      value === "referral" ? referralBalance ?? 0 : selected?.profit ?? 0;
    const numericProfit = Number(rawProfit);
    const totalProfit = Number.isFinite(numericProfit) ? Math.max(numericProfit, 0) : 0;
    setForm((prev) => ({
      ...prev,
      challenge_account: value,
      total_profit: totalProfit,
    }));
  };

  const buildDescription = () => {
    return form.description?.trim() || "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        source_account: form.challenge_account || null,
        withdrawal_type: form.withdrawal_type,
        account_number: form.account_number,
        wallet_address: form.wallet_address,
        total_profit: form.total_profit,
        withdrawable_amount: form.withdrawable_amount,
        description: buildDescription(),
      };
      await createWithdrawal(payload);
      setMessage("درخواست برداشت با موفقیت ثبت شد.");
      setForm({
        withdrawal_type: "bank_transfer",
        account_number: "",
        wallet_address: "",
        challenge_account: "",
        total_profit: "",
        withdrawable_amount: "",
        description: "",
      });
      loadData();
    } catch (err) {
      setError(err.message || "خطا در ثبت درخواست برداشت");
    } finally {
      setLoading(false);
    }
  };

  const verifiedAddresses = addresses.filter((addr) => addr.is_verified);
  const hasAddresses = verifiedAddresses.length > 0;
  const referralOptionLabel = `حساب همکار (موجودی: ${formatBalance(referralBalance)})`;

  return (
    <MenuLayout>
      <div className="withdraw-page">
        <header className="withdraw-header">
          <h1 className="withdraw-title">برداشت سود</h1>
          <p className="withdraw-subtitle">
            برای برداشت سود، اطلاعات لازم را با دقت وارد کنید.
          </p>
        </header>

        <div className="withdraw-steps">
          <div className="withdraw-step-item">
            <span className="withdraw-step-number">1</span>
            <span className="withdraw-step-text">
              قبل از ثبت درخواست برداشت، اطلاعات خود را بررسی کنید تا بدون مشکل پردازش شود.
            </span>
          </div>
          <div className="withdraw-step-item">
            <span className="withdraw-step-number">2</span>
            <span className="withdraw-step-text">
              مبلغ برداشت باید با موجودی قابل برداشت شما همخوانی داشته باشد.
            </span>
          </div>
          <div className="withdraw-step-item">
            <span className="withdraw-step-number">3</span>
            <span className="withdraw-step-text">
              در صورت برداشت بانکی حتما حساب بانکی به اسم خودتان باشد.
            </span>
          </div>
          <div className="withdraw-step-item">
            <span className="withdraw-step-number">4</span>
            <span className="withdraw-step-text">
              در صورت برداشت بصورت ارز دیجیتال، حتما قبل از این کار در صفحه کیف پول با کلیک روی اضافه کردن آدرس جدید آدرس خود را وارد کرده باشید.
            </span>
          </div>
          <div className="withdraw-step-item">
            <span className="withdraw-step-number">5</span>
            <span className="withdraw-step-text">
              سود کل نوشته شده در پایین، سود کل حساب شماست و بعد از درخواست برداشت بر اساس درصد Profit share مختص حسابتان مبلغ برای شما واریز می‌شود.
            </span>
          </div>
        </div>

        <form className="withdraw-form" onSubmit={handleSubmit}>
          <div className="withdraw-grid">
            <label className="withdraw-field">
              <span>نوع برداشت:</span>
              <select
                value={form.withdrawal_type}
                onChange={(e) => handleChange("withdrawal_type", e.target.value)}
              >
                <option value="bank_transfer">واریز بانکی</option>
                <option value="crypto">کیف پول کریپتو</option>
              </select>
            </label>
            <label className="withdraw-field">
              <span>شماره حساب:</span>
              <select
                value={form.challenge_account}
                onChange={(e) => handleSourceAccountChange(e.target.value)}
              >
                <option value="">انتخاب شماره حساب</option>
                {challengeAccounts.map((account) => (
                  <option key={account.id || account.account_number} value={account.account_number}>
                    {account.account_number}
                  </option>
                ))}
                <option value="referral">{referralOptionLabel}</option>
              </select>
            </label>
            <label className="withdraw-field">
              <span>شماره حساب شبا (در صورت برداشت بانکی):</span>
              <input
                type="text"
                value={form.account_number}
                onChange={(e) => handleChange("account_number", e.target.value)}
                placeholder="IR000000000000000000000000"
              />
            </label>
            <label className="withdraw-field">
              <span>آدرس کیف پول (در صورت برداشت کریپتو):</span>
              <select
                value={form.wallet_address}
                onChange={(e) => handleChange("wallet_address", e.target.value)}
              >
                <option value="">
                  {hasAddresses ? "انتخاب آدرس" : "ابتدا از کیف پول آدرس اضافه کنید"}
                </option>
                {verifiedAddresses.map((addr) => (
                  <option key={addr.id} value={addr.address}>
                    {addr.address}
                  </option>
                ))}
              </select>
            </label>
            <label className="withdraw-field">
              <span>سود کل:</span>
              <input
                type="number"
                value={form.total_profit}
                placeholder="0.00"
                readOnly
              />
            </label>
            <label className="withdraw-field">
              <span>مبلغ برداشت:</span>
              <input
                type="number"
                value={form.withdrawable_amount}
                onChange={(e) => handleChange("withdrawable_amount", e.target.value)}
                placeholder="0.00"
              />
            </label>
          </div>

          <label className="withdraw-field withdraw-textarea">
            <span>توضیحات:</span>
            <textarea
              rows="4"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="توضیحات تکمیلی"
            ></textarea>
          </label>

          {error && <div className="withdraw-error">{error}</div>}
          {message && <div className="withdraw-success">{message}</div>}

          <button type="submit" className="withdraw-submit" disabled={loading}>
            {loading ? "در حال ثبت..." : "ثبت درخواست برداشت"}
          </button>
        </form>

        {requests.length > 0 && (
          <div className="withdraw-list">
            <div className="withdraw-list-head">
              <span>نوع</span>
              <span>مبلغ برداشت</span>
              <span>وضعیت</span>
              <span>تاریخ</span>
            </div>
            <div className="withdraw-list-body">
              {requests.map((r, idx) => (
                <div className="withdraw-list-row" key={r.id || idx}>
                  <span>{r.withdrawal_type === "bank_transfer" ? "واریز بانکی" : "کیف پول کریپتو"}</span>
                  <span>{r.withdrawable_amount}</span>
                  <span className="withdraw-status">{r.status}</span>
                  <span>{r.created_at}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MenuLayout>
  );
};

export default WithdrawPage;
